import * as Tone from 'tone';
import type { InstrumentId } from '../config/sample-maps';
import { SAMPLE_BASE_URL, SAMPLE_MAPS } from '../config/sample-maps';
import { parseNoteName, toneName } from '../config/music';
import type { SampleDensity } from '../state/settings';
import { ensureGraph } from './graph';

/**
 * Sample loading is done here rather than through tonejs-instruments'
 * SampleLibrary.load(), which is not usable as shipped:
 *
 *  - Its default file extension is the literal placeholder '.[mp3|ogg]', so
 *    unless setExt() is called first every request 404s. That is why the
 *    "sample real" engine produced no sound at all.
 *  - It mutates its own note tables in place (both setExt and minify), so the
 *    second load of an instrument reads a corrupted table.
 *  - It loads every sample: 85 files and roughly 21 MB for the piano alone,
 *    with no progress feedback and a decode storm that freezes the tab.
 *
 * The note→filename tables are vendored as plain data in config/sample-maps.ts
 * and the Tone.Sampler is built here, from a copy, with a density limit,
 * bounded fetch concurrency, stale-load cancellation and progress reporting.
 */

export interface SampleEntry {
  readonly note: string;
  readonly abs: number;
  readonly file: string;
}

/** Pick a subset with at least `step` semitones between consecutive samples. */
export function pickSamples(instrument: InstrumentId, step: SampleDensity): SampleEntry[] {
  const table = SAMPLE_MAPS[instrument] as Record<string, string> | undefined;
  if (!table) return [];

  const all: SampleEntry[] = Object.entries(table)
    .map(([note, file]) => ({ note, abs: parseNoteName(note) ?? -1, file }))
    .filter((s) => s.abs >= 0)
    .sort((a, b) => a.abs - b.abs);

  if (step <= 1 || all.length <= 3) return all;

  const out: SampleEntry[] = [];
  for (const s of all) {
    const last = out[out.length - 1];
    if (!last || s.abs - last.abs >= step) out.push(s);
  }
  // Always keep the topmost sample so the highest octave is not resampled
  // from far below it.
  const highest = all[all.length - 1];
  if (highest && out[out.length - 1] !== highest) out.push(highest);
  return out;
}

async function mapLimit<T, R>(items: readonly T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      const item = items[i];
      if (item === undefined) return;
      results[i] = await fn(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

export type SamplerStatus =
  | { kind: 'idle' }
  | { kind: 'loading'; instrument: InstrumentId; loaded: number; total: number }
  | {
      kind: 'ready';
      instrument: InstrumentId;
      count: number;
      low: string;
      high: string;
      failed: number;
      cached: boolean;
      /** True while a coarse pass is playable but the full density is still loading. */
      upgrading: boolean;
      /** The density level currently in use, for the density selector to reflect. */
      density: SampleDensity;
    }
  | { kind: 'error'; instrument: InstrumentId; code: 'noSamples' | 'loadFailed' };

type StatusListener = (s: SamplerStatus) => void;
const statusListeners = new Set<StatusListener>();
let status: SamplerStatus = { kind: 'idle' };

export function onSamplerStatus(fn: StatusListener): () => void {
  statusListeners.add(fn);
  fn(status);
  return () => statusListeners.delete(fn);
}

function setStatus(s: SamplerStatus): void {
  status = s;
  for (const fn of statusListeners) fn(s);
}

export const getSamplerStatus = (): SamplerStatus => status;

const cache = new Map<string, Tone.Sampler>();
let attached: Tone.Sampler | null = null;
let attachedKey: string | null = null;
let loadToken = 0;

export const currentSampler = (): Tone.Sampler | null => attached;
export const isSamplerReady = (): boolean => attached !== null;

function attach(sampler: Tone.Sampler, key: string, attack: number, release: number): void {
  const g = ensureGraph();
  if (attached && attached !== sampler) {
    try {
      attached.disconnect();
    } catch {
      /* ignore */
    }
  }
  sampler.attack = Math.max(0, attack);
  sampler.release = Math.max(0.01, release);
  sampler.connect(g.sourceTrim);
  attached = sampler;
  attachedKey = key;
}

export function detachSampler(): void {
  if (attached) {
    try {
      attached.disconnect();
    } catch {
      /* ignore */
    }
  }
  attached = null;
  attachedKey = null;
  loadToken++;
  setStatus({ kind: 'idle' });
}

export function setSamplerEnvelope(attack: number, release: number): void {
  if (!attached) return;
  attached.attack = Math.max(0, attack);
  attached.release = Math.max(0.01, release);
}

/** Coarsest density — loaded first so the keyboard becomes playable quickly. */
const QUICK_DENSITY: SampleDensity = 6;

/** Hard cap on the coarse pass so it is a handful of files at most — instant. */
const QUICK_MAX_SAMPLES = 6;

/** ~QUICK_MAX_SAMPLES evenly-spaced samples spanning the range, for a fast start. */
function pickSamplesQuick(instrument: InstrumentId): SampleEntry[] {
  const all = pickSamples(instrument, 1); // every available sample, sorted
  if (all.length <= QUICK_MAX_SAMPLES) return all;
  const out: SampleEntry[] = [];
  const step = (all.length - 1) / (QUICK_MAX_SAMPLES - 1);
  for (let i = 0; i < QUICK_MAX_SAMPLES; i++) {
    const entry = all[Math.round(i * step)];
    if (entry && out[out.length - 1] !== entry) out.push(entry);
  }
  return out;
}

/**
 * Load (or reuse) a specific set of sample files under `key`, attach it, and
 * report it as the given `density` level. `key` also namespaces the cache.
 */
async function loadEntries(
  instrument: InstrumentId,
  entries: readonly SampleEntry[],
  key: string,
  density: SampleDensity,
  attack: number,
  release: number,
  token: number,
  opts: { upgrading: boolean; announceLoading: boolean },
): Promise<boolean> {
  const cached = cache.get(key);
  if (cached) {
    if (token !== loadToken) return false;
    attach(cached, key, attack, release);
    const notes = describe(key);
    setStatus({ kind: 'ready', instrument, count: notes.count, low: notes.low, high: notes.high, failed: 0, cached: true, upgrading: opts.upgrading, density });
    return true;
  }

  if (entries.length === 0) {
    if (opts.announceLoading) setStatus({ kind: 'error', instrument, code: 'noSamples' });
    return false;
  }

  if (opts.announceLoading) setStatus({ kind: 'loading', instrument, loaded: 0, total: entries.length });

  const rawCtx = Tone.getContext().rawContext as unknown as BaseAudioContext;
  let loaded = 0;
  let failed = 0;

  const decoded = await mapLimit(entries, 6, async (f) => {
    // A superseded load stops issuing new requests here — cheaper and quieter
    // than aborting in-flight ones (which the browser logs as console errors),
    // so a large cancelled instrument stops downloading almost at once.
    if (token !== loadToken) return null;
    try {
      const res = await fetch(`${SAMPLE_BASE_URL}${instrument}/${f.file}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buffer = await rawCtx.decodeAudioData(await res.arrayBuffer());
      loaded++;
      if (token === loadToken && opts.announceLoading) setStatus({ kind: 'loading', instrument, loaded, total: entries.length });
      return { note: f.note, buffer };
    } catch (err) {
      failed++;
      console.warn(`Sample eșuat: ${instrument}/${f.file}`, err);
      return null;
    }
  });

  // The user may have switched instrument or engine while this was in flight;
  // a stale load must never replace the live one.
  if (token !== loadToken) return false;

  const urls: Record<string, AudioBuffer> = {};
  for (const d of decoded) if (d) urls[d.note] = d.buffer;

  const noteNames = Object.keys(urls);
  if (noteNames.length === 0) {
    if (opts.announceLoading) setStatus({ kind: 'error', instrument, code: 'loadFailed' });
    return false;
  }

  const sampler = new Tone.Sampler({ urls });
  cache.set(key, sampler);
  attach(sampler, key, attack, release);

  const info = describe(key, noteNames);
  setStatus({ kind: 'ready', instrument, count: info.count, low: info.low, high: info.high, failed, cached: false, upgrading: opts.upgrading, density });
  return true;
}

/**
 * Two-pass load: a handful of samples first, so the keyboard is playable almost
 * at once (reported as the coarse "Rapid" density), then the full target density
 * in the background, swapped in when ready. The background pass stays silent (no
 * 'loading' status) so it never re-gates the keyboard.
 */
export async function ensureSampler(
  instrument: InstrumentId,
  density: SampleDensity,
  attack: number,
  release: number,
): Promise<void> {
  ensureGraph();
  const targetKey = `${instrument}|${density}`;
  if (attachedKey === targetKey && attached) return;

  const token = ++loadToken;

  const fullEntries = pickSamples(instrument, density);
  const quickEntries = pickSamplesQuick(instrument);

  // Skip the coarse pass when the target is already tiny (the quick set would
  // be the same files, or the target is the coarsest density anyway).
  if (density >= QUICK_DENSITY || fullEntries.length <= quickEntries.length) {
    await loadEntries(instrument, fullEntries, targetKey, density, attack, release, token, { upgrading: false, announceLoading: true });
    return;
  }

  const quickKey = `${instrument}|quick`;
  const quickOk = await loadEntries(instrument, quickEntries, quickKey, QUICK_DENSITY, attack, release, token, { upgrading: true, announceLoading: true });
  if (token !== loadToken) return;

  const fullOk = await loadEntries(instrument, fullEntries, targetKey, density, attack, release, token, { upgrading: false, announceLoading: !quickOk });
  if (token !== loadToken) return;

  // Full upgrade failed but the coarse pass is playable — settle the status so
  // it does not read as "still upgrading" forever.
  if (!fullOk && quickOk) {
    const info = describe(quickKey);
    setStatus({ kind: 'ready', instrument, count: info.count, low: info.low, high: info.high, failed: 0, cached: true, upgrading: false, density: QUICK_DENSITY });
  }
}

const rangeCache = new Map<string, { count: number; low: string; high: string }>();

function describe(key: string, noteNames?: readonly string[]): { count: number; low: string; high: string } {
  const known = rangeCache.get(key);
  if (known) return known;
  const names = noteNames ?? [];
  const abs = names.map((n) => parseNoteName(n) ?? 0).sort((a, b) => a - b);
  const info = {
    count: names.length,
    low: toneName(abs[0] ?? 0),
    high: toneName(abs[abs.length - 1] ?? 0),
  };
  rangeCache.set(key, info);
  return info;
}
